/**
 * PortalFbarAccounts.jsx
 * Client-facing portal form for FBAR Account Information.
 * URL: /portal/fbar/:engagementId/:year
 *
 * Canonical scope: one component per (engagement_id, reporting_year).
 * Allows: add / edit / delete accounts, autosave, final certification.
 * No foreign account statement requirement (firm policy).
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { portalApi } from "@/lib/portalApi";
import {
  Globe, Plus, Trash2, ChevronDown, ChevronUp,
  CheckCircle2, AlertCircle, Loader2, ArrowLeft,
} from "lucide-react";

const REQUIRED_FIELDS = ["institution_name", "city", "country", "account_number", "highest_balance"];

function accountIsComplete(acc) {
  return REQUIRED_FIELDS.every(
    (f) => acc[f] !== null && acc[f] !== undefined && acc[f] !== ""
  );
}

const EMPTY_ACCOUNT = {
  institution_name: "",
  city: "",
  state_province: "",
  country: "",
  account_number: "",
  highest_balance: "",
  currency: "",
};

function AccountCard({ account, index, onUpdate, onDelete, saving }) {
  const [expanded, setExpanded] = useState(true);
  const [local, setLocal] = useState(account);
  const saveTimer = useRef(null);
  const complete = accountIsComplete(local);

  const handleChange = useCallback((field, value) => {
    const updated = { ...local, [field]: value };
    setLocal(updated);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      onUpdate(account.id, updated);
    }, 800);
  }, [local, account.id, onUpdate]);

  return (
    <div
      className={`border rounded-xl overflow-hidden bg-white ${
        complete ? "border-emerald-200" : "border-slate-200"
      }`}
      data-testid={`fbar-account-card-${index}`}
    >
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-3">
          {complete ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0" />
          )}
          <div>
            <p className="font-semibold text-slate-800 text-sm" data-testid={`fbar-account-title-${index}`}>
              {local.institution_name || `Foreign Financial Account ${index + 1}`}
            </p>
            {local.country && (
              <p className="text-xs text-slate-400">{local.country}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(account.id);
            }}
            className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
            data-testid={`fbar-delete-account-${index}`}
            title="Remove this account"
            disabled={saving}
          >
            <Trash2 className="h-4 w-4" />
          </button>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Financial Institution Name"
            required
            value={local.institution_name}
            onChange={(v) => handleChange("institution_name", v)}
            placeholder="e.g. Bangkok Bank"
            testId={`fbar-field-institution-${index}`}
          />
          <Field
            label="Account Number"
            required
            value={local.account_number}
            onChange={(v) => handleChange("account_number", v)}
            placeholder="e.g. 123456789"
            testId={`fbar-field-account-number-${index}`}
          />
          <Field
            label="City"
            required
            value={local.city}
            onChange={(v) => handleChange("city", v)}
            placeholder="e.g. Bangkok"
            testId={`fbar-field-city-${index}`}
          />
          <Field
            label="State / Province"
            value={local.state_province}
            onChange={(v) => handleChange("state_province", v)}
            placeholder="e.g. Bangkok (optional)"
            testId={`fbar-field-state-${index}`}
          />
          <Field
            label="Country"
            required
            value={local.country}
            onChange={(v) => handleChange("country", v)}
            placeholder="e.g. Thailand"
            testId={`fbar-field-country-${index}`}
          />
          <Field
            label={`Highest Balance During ${account.reporting_year || "the Year"}`}
            required
            type="number"
            value={local.highest_balance}
            onChange={(v) => handleChange("highest_balance", v)}
            placeholder="e.g. 850000"
            testId={`fbar-field-balance-${index}`}
          />
          <Field
            label="Currency (optional)"
            value={local.currency}
            onChange={(v) => handleChange("currency", v)}
            placeholder="e.g. THB, USD"
            testId={`fbar-field-currency-${index}`}
          />
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, required, type = "text", testId }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white text-slate-800 placeholder:text-slate-300"
      />
    </div>
  );
}

export default function PortalFbarAccounts() {
  const { engagementId, year } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);    // {certified, account_count, accounts, all_complete}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [certifying, setCertifying] = useState(false);
  const [certConfirm, setCertConfirm] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await portalApi.get(`/portal/fbar/${engagementId}/${year}/status`);
      setStatus(data);
      if (data.certified) setCertConfirm(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load FBAR information.");
    } finally {
      setLoading(false);
    }
  }, [engagementId, year]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleAddAccount = async () => {
    setSaving(true);
    try {
      const { data } = await portalApi.post(
        `/portal/fbar/${engagementId}/${year}/accounts`,
        EMPTY_ACCOUNT
      );
      setStatus((prev) => ({
        ...prev,
        certified: false,
        accounts: [...(prev?.accounts || []), { ...data, reporting_year: year }],
        account_count: (prev?.account_count || 0) + 1,
      }));
      setCertConfirm(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to add account.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAccount = useCallback(async (accountId, updates) => {
    setSaving(true);
    try {
      const { data } = await portalApi.put(
        `/portal/fbar/${engagementId}/${year}/accounts/${accountId}`,
        updates
      );
      setStatus((prev) => ({
        ...prev,
        certified: false,
        accounts: (prev?.accounts || []).map((a) =>
          a.id === accountId ? { ...a, ...data } : a
        ),
        all_complete: false,
      }));
      setCertConfirm(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save account.");
    } finally {
      setSaving(false);
    }
  }, [engagementId, year]);

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm("Remove this account?")) return;
    setSaving(true);
    try {
      await portalApi.delete(
        `/portal/fbar/${engagementId}/${year}/accounts/${accountId}`
      );
      setStatus((prev) => {
        const updated = (prev?.accounts || []).filter((a) => a.id !== accountId);
        return { ...prev, accounts: updated, account_count: updated.length, certified: false };
      });
      setCertConfirm(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to remove account.");
    } finally {
      setSaving(false);
    }
  };

  const handleCertify = async () => {
    if (!certConfirm) {
      setError("Please check the confirmation checkbox first.");
      return;
    }
    setCertifying(true);
    setError(null);
    try {
      await portalApi.post(`/portal/fbar/${engagementId}/${year}/certify`);
      setStatus((prev) => ({ ...prev, certified: true }));
    } catch (e) {
      setError(e.response?.data?.detail || "Certification failed. Please ensure all accounts are complete.");
    } finally {
      setCertifying(false);
    }
  };

  const handleUncertify = async () => {
    try {
      await portalApi.delete(`/portal/fbar/${engagementId}/${year}/certification`);
      setStatus((prev) => ({ ...prev, certified: false }));
      setCertConfirm(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to retract certification.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  const accounts = status?.accounts || [];
  const allComplete = accounts.length > 0 && accounts.every(accountIsComplete);
  const canCertify = allComplete && certConfirm && !status?.certified;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fbar-accounts-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate("/portal")}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="fbar-back-button"
          >
            <ArrowLeft className="h-4 w-4 text-slate-500" />
          </button>
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            <span className="font-semibold text-slate-800 text-sm">
              Complete Your {year} FBAR Account Information
            </span>
          </div>
          {saving && (
            <div className="ml-auto flex items-center gap-1.5 text-xs text-slate-400">
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving…
            </div>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Intro */}
        {!status?.certified && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl px-5 py-4">
            <p className="text-sm text-blue-800">
              Please provide the details for each foreign financial account that is reportable
              for your {year} FBAR. Your name, date of birth, and address are already on file.
            </p>
          </div>
        )}

        {/* Certified banner */}
        {status?.certified && (
          <div
            className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex items-start gap-3"
            data-testid="fbar-certified-banner"
          >
            <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-emerald-800 text-sm">Submitted</p>
              <p className="text-sm text-emerald-700">
                You have certified your {year} FBAR account information is complete.
                Your firm is now preparing your FBAR.
              </p>
              <button
                onClick={handleUncertify}
                className="text-xs text-emerald-600 underline mt-1 hover:text-emerald-800"
                data-testid="fbar-uncertify-button"
              >
                I need to make changes
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-start gap-2"
            data-testid="fbar-error-message"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Account cards */}
        <div className="space-y-3" data-testid="fbar-accounts-list">
          {accounts.map((acc, i) => (
            <AccountCard
              key={acc.id}
              account={{ ...acc, reporting_year: year }}
              index={i}
              onUpdate={handleUpdateAccount}
              onDelete={handleDeleteAccount}
              saving={saving}
            />
          ))}
        </div>

        {/* Add account button */}
        {!status?.certified && (
          <button
            onClick={handleAddAccount}
            disabled={saving}
            className="w-full py-3 px-4 rounded-xl border-2 border-dashed border-blue-200 text-blue-600 text-sm font-medium hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="fbar-add-account-button"
          >
            <Plus className="h-4 w-4" />
            Add Another Account
          </button>
        )}

        {/* No accounts */}
        {accounts.length === 0 && !status?.certified && (
          <div className="text-center py-8 text-slate-400">
            <Globe className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No accounts added yet.</p>
            <p className="text-xs mt-1">
              Use the button above to add each foreign financial account.
            </p>
          </div>
        )}

        {/* Certification */}
        {accounts.length > 0 && !status?.certified && (
          <div
            className={`border rounded-xl px-5 py-5 space-y-4 ${
              allComplete ? "border-slate-200 bg-white" : "border-amber-100 bg-amber-50"
            }`}
            data-testid="fbar-certification-section"
          >
            {!allComplete && (
              <div className="flex items-start gap-2 text-amber-700 text-xs">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                Please complete all required fields before certifying.
              </div>
            )}

            <label
              className={`flex items-start gap-3 cursor-pointer ${
                !allComplete ? "opacity-50 pointer-events-none" : ""
              }`}
              data-testid="fbar-certify-checkbox-label"
            >
              <input
                type="checkbox"
                checked={certConfirm}
                onChange={(e) => setCertConfirm(e.target.checked)}
                disabled={!allComplete}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                data-testid="fbar-certify-checkbox"
              />
              <span className="text-sm text-slate-700 leading-snug">
                I confirm that I have listed{" "}
                <strong>all foreign financial accounts</strong> required for my {year} FBAR.
              </span>
            </label>

            <button
              onClick={handleCertify}
              disabled={!canCertify || certifying || saving}
              className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              data-testid="fbar-submit-button"
            >
              {certifying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                "Submit FBAR Information"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
